import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

const priceListData = {
  data: {
    Response: {
      response_code: "",
      response_message: "",
    },
    data: {},
  },
  rowcount: 0,
  active: "active",
};

const base_url = process.env.REACT_APP_BACKEND_URL;

function TOCPricing() {
  const [priceList, setPriceList] = useState<any | null>(priceListData);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const settings: any = useSelector<any>((state) => state.settings);
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const bannerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Get header height dynamically (accounting for sticky header)
  const getHeaderHeight = () => {
    const header = document.querySelector('header');
    if (header) {
      // Use getBoundingClientRect for more accurate height including margins
      const rect = header.getBoundingClientRect();
      // For sticky headers, we need the full height
      return rect.height;
    }
    // Fallback for large sticky headers
    return 150;
  };

  useEffect(() => {
    let currentShopId = localStorage.getItem("currentShopHome") || null;
    const url = new URL(window.location.href);
    const queryParamsShopId = url.searchParams.get("shop_id");
    const url_array = url.pathname.split("/").filter(Boolean);
    let shop_name = url_array[1];
    if (queryParamsShopId) {
      shop_name = queryParamsShopId;
      currentShopId = queryParamsShopId;
    }

    const headers = {
      Accept: "application/json",
    };

    axios
      .get(`${base_url}/api/get_categories?group_id=1&shop_name=${shop_name}&shop_id=${currentShopId}`, {
        headers: headers,
      })
      .then((e) => {
        setPriceList({ data: e.data });
        if (e.data.data.category && e.data.data.category.length > 0) {
          setActiveCategory(e.data.data.category[0].main_category_name);
        }
      });
  }, []);

  // Scroll to category on mount if hash exists in URL
  useEffect(() => {
    if (priceList.data?.data?.category) {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setTimeout(() => {
          const hashBannerId = hash.replace('category-', 'banner-');
          const bannerElement = document.getElementById(hashBannerId) || bannerRefs.current[hashBannerId];
          const sectionElement = categoryRefs.current[hash];
          const element = bannerElement || sectionElement;
          
          if (element) {
            const headerHeight = getHeaderHeight();
            const elementTop = element.offsetTop;
            const targetScrollPosition = elementTop - headerHeight - 120;
            requestAnimationFrame(() => {
              window.scrollTo({
                top: Math.max(0, targetScrollPosition),
                behavior: 'smooth'
              });
            });
          }
        }, 300);
      }
    }
  }, [priceList]);

  const handleCategoryClick = (categoryName: string) => {
    setActiveCategory(categoryName);
    // Sanitize category name for ID (handle special characters)
    const sanitized = categoryName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').toLowerCase();
    const categoryId = `category-${sanitized}`;
    const bannerId = `banner-${sanitized}`;
    
    // Use requestAnimationFrame and setTimeout to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Try to get the banner element first (preferred), then fallback to section
        const bannerElement = document.getElementById(bannerId) || bannerRefs.current[bannerId];
        const sectionElement = document.getElementById(categoryId) || categoryRefs.current[categoryId];
        const element = bannerElement || sectionElement;
        
        if (element) {
          const headerHeight = getHeaderHeight();
          // Calculate the absolute position of the element from the top of the document
          const elementTop = element.offsetTop;
          
          // We want to scroll so the banner appears fully below the sticky header
          // Target position: header height + padding (120px) below header for excellent visibility
          const targetScrollPosition = elementTop - headerHeight - 120;

          // Use requestAnimationFrame for smoother scroll
          requestAnimationFrame(() => {
            window.scrollTo({
              top: Math.max(0, targetScrollPosition),
              behavior: 'smooth'
            });
          });
        }
      }, 100);
    });
  };

  const distributeItems = (items: any[], columns: number = 3) => {
    const result: any[][] = Array(columns).fill(null).map(() => []);
    items.forEach((item, index) => {
      result[index % columns].push(item);
    });
    return result;
  };

  return (
    <div className="toc-pricing-section">
      <div className="container">
        {/* Category Navigation */}
        <div className="toc-pricing-categories">
          <div className="row">
            <div className="col-md-12">
              <div className="toc-category-nav">
                {priceList.data?.data?.category?.map(
                  (category: any, catindex: any) => (
                    <button
                      key={catindex}
                      className={`toc-category-btn ${activeCategory === category.main_category_name ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category.main_category_name)}
                    >
                      {category.main_category_name}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Lists */}
        <div className="toc-pricing-lists">
          {priceList.data?.data?.category?.map(
            (category: any, catindex: any) => {
              // Sanitize category name for ID (handle special characters)
              const sanitized = category.main_category_name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').toLowerCase();
              const categoryId = `category-${sanitized}`;
              const subCategories = category.sub_categories || [];
              const distributedItems = distributeItems(subCategories, 3);

              return (
                <div
                  key={catindex}
                  id={categoryId}
                  ref={(el) => (categoryRefs.current[categoryId] = el)}
                  className="toc-category-section"
                >
                  {/* Category Banner */}
                  <div 
                    id={`banner-${sanitized}`}
                    ref={(el) => (bannerRefs.current[`banner-${sanitized}`] = el)}
                    className="toc-category-banner"
                  >
                    <h2>{category.main_category_name}</h2>
                  </div>

                  {/* Pricing Items in Columns */}
                  <div className="toc-pricing-items">
                    <div className="row">
                      {distributedItems.map((column, colIndex) => (
                        <div key={colIndex} className="col-md-4 col-sm-6">
                          <ul className="toc-price-list">
                            {column.map((sub_category: any, subcatindex: any) => (
                              <li key={subcatindex} className="toc-price-item">
                                <div className="toc-price-name">
                                  {sub_category.sub_category_name}
                                </div>
                                <div className="toc-price-amount">
                                  {settings?.WebmasterSettings?.currency}
                                  {sub_category.total_price}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}

export default TOCPricing;

